"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { useChart } from "./chart-context";
export interface BarXAxisProps {
  /** Width of the date ticker box for fade calculation. Default: 50 */
  tickerHalfWidth?: number;
  /** Whether to show all labels or skip some for dense data. Default: false */
  showAllLabels?: boolean;
  /** Maximum number of labels to show. Default: 12 */
  maxLabels?: number;
  /** Optional data key to use for the displayed label while xDataKey remains unique. */
  labelDataKey?: string;
}
interface BarXAxisLabelProps {
  label: string;
  x: number;
  crosshairX: number | null;
  isHovering: boolean;
  tickerHalfWidth: number;
}
function BarXAxisLabel(t0) {
  const $ = _c(13);
  const {
    label,
    x,
    crosshairX,
    isHovering,
    tickerHalfWidth
  } = t0;
  const fadeRadius = tickerHalfWidth + 20;
  let opacity = 1;
  if (isHovering && crosshairX !== null) {
    const distance = Math.abs(x - crosshairX);
    if (distance < tickerHalfWidth) {
      opacity = 0;
    } else {
      if (distance < fadeRadius) {
        opacity = (distance - tickerHalfWidth) / 20;
      }
    }
  }
  let t1;
  if ($[0] !== x) {
    t1 = {
      left: x,
      bottom: 12,
      width: 0,
      display: "flex",
      justifyContent: "center"
    };
    $[0] = x;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== opacity) {
    t2 = {
      opacity
    };
    $[2] = opacity;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("text-chart-label whitespace-nowrap text-xs");
    t4 = {
      opacity: 1
    };
    t5 = {
      duration: 0.4,
      ease: "easeInOut"
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label || $[8] !== t2) {
    t6 = <motion.span animate={t2} className={t3} initial={t4} transition={t5}>{label}</motion.span>;
    $[7] = label;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t6) {
    t7 = <div className="absolute" style={t1}>{t6}</div>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export function BarXAxis({
  tickerHalfWidth = 50,
  showAllLabels = false,
  maxLabels = 12,
  labelDataKey
}: BarXAxisProps) {
  const {
    margin,
    tooltipData,
    containerRef,
    barScale,
    bandWidth,
    barXAccessor,
    data
  } = useChart();
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  // Sync portal container (adjust state during render)
  if (containerRef.current && containerRef.current !== portalContainer) {
    setPortalContainer(containerRef.current);
  }

  // Generate labels for each bar
  const labelsToShow = useMemo(() => {
    if (!(barScale && bandWidth && barXAccessor)) {
      return [];
    }
    const allLabels = data.map(d => {
      const category = barXAccessor(d);
      const label = labelDataKey ? String(d[labelDataKey] ?? category) : category;
      const bandX = barScale(category) ?? 0;
      // Center the label under the bar group
      const x = bandX + bandWidth / 2 + margin.left;
      return {
        label,
        x
      };
    });

    // If showAllLabels is true or we have fewer than maxLabels, show all
    if (showAllLabels || allLabels.length <= maxLabels) {
      return allLabels;
    }

    // Otherwise, skip some labels to avoid crowding
    const step = Math.ceil(allLabels.length / maxLabels);
    return allLabels.filter((_, i) => i % step === 0);
  }, [barScale, bandWidth, barXAccessor, data, margin.left, showAllLabels, maxLabels, labelDataKey]);
  const isHovering = tooltipData !== null;
  const crosshairX = tooltipData ? tooltipData.x + margin.left : null;

  // Use portal to render into the chart container
  if (!portalContainer) {
    return null;
  }

  // Early return if not in a BarChart
  if (!barScale) {
    return null;
  }
  return createPortal(<div className="pointer-events-none absolute inset-0">
      {labelsToShow.map(item => <BarXAxisLabel crosshairX={crosshairX} isHovering={isHovering} key={`${item.label}-${item.x}`} label={item.label} tickerHalfWidth={tickerHalfWidth} x={item.x} />)}
    </div>, portalContainer);
}
BarXAxis.displayName = "BarXAxis";
export default BarXAxis;